import { EvaluacionDetalle, Reactivo, ReactivoOpcion, Seccion } from '@/hooks/useEvaluacion';

export interface ReactivoConOpciones {
    reactivo: Reactivo;
    opciones: ReactivoOpcion[];
    /**
     * Preindexa las opciones por nOpcion para que los lookups durante el render
     * (ponderación, validación de obligatorios, búsqueda de la opción
     * seleccionada en ReactivoRow) sean O(1) en lugar de O(O) por reactivo. Se
     * construye una sola vez en `agruparSecciones` y se reusa en cada
     * recompute de `respuestas`.
     */
    opcionesByOpcion: Map<number, ReactivoOpcion>;
}

export interface SeccionConReactivos {
    seccion: Seccion;
    reactivos: ReactivoConOpciones[];
    /**
     * Lista plana de reactivos de la sección. Permite alimentar a los helpers
     * memoizados (contarObligatoriosPendientes, contarRespondiblesRespondidos,
     * etc.) sin reasignar un array nuevo en cada llamada con `flatMap`.
     */
    reactivosFlat: ReactivoConOpciones[];
}

// bMostrarSeccion llega como string '1'/'0' desde la BD, se normaliza a string.
export const mostrarHeaderSeccion = (seccion: Seccion) => String(seccion.bMostrarSeccion) === '1';

// Agrupa la evaluación por secciones activas (ordenadas por nSeccion); dentro de
// cada sección ordena los reactivos por nReactivo y cada reactivo con sus
// opciones activas ordenadas por nOpcion. Además, por cada reactivo preindexa
// las opciones en un Map (nOpcion -> opcion) para que los lookups durante el
// render sean O(1) — antes eran O(O) por reactivo y se ejecutaban en cada
// keystroke del usuario, causando lag perceptible con varios reactivos.
export const agruparSecciones = (evaluacion: EvaluacionDetalle): SeccionConReactivos[] => {
    const secciones = [...(evaluacion.Secciones ?? [])]
        .filter((seccion) => seccion.bActivo)
        .sort((a, b) => a.nSeccion - b.nSeccion);

    const reactivosActivos = (evaluacion.Reactivos ?? []).filter((reactivo) => reactivo.bActivo);
    const opcionesActivas = (evaluacion.ReactivosOpciones ?? []).filter((opcion) => opcion.bActivo);

    return secciones.map((seccion) => {
        const reactivos: ReactivoConOpciones[] = reactivosActivos
            .filter((reactivo) => reactivo.nSeccion === seccion.nSeccion)
            .sort((a, b) => a.nReactivo - b.nReactivo)
            .map((reactivo) => {
                const opciones = opcionesActivas
                    .filter(
                        (opcion) =>
                            opcion.nSeccion === reactivo.nSeccion
                            && opcion.nReactivo === reactivo.nReactivo,
                    )
                    .sort((a, b) => a.nOpcion - b.nOpcion);
                const opcionesByOpcion = new Map<number, ReactivoOpcion>();
                for (const opcion of opciones) {
                    opcionesByOpcion.set(opcion.nOpcion, opcion);
                }
                return { reactivo, opciones, opcionesByOpcion };
            });

        return {
            seccion,
            reactivos,
            reactivosFlat: reactivos,
        };
    });
};

export interface PonderacionSeccion {
    total: number;
    obtenido: number;
}

// Respuesta local de un reactivo. Vive en el estado del view (key
// `${nSeccion}-${nReactivo}`) y es el shape que consume el constructor de payload.
export interface RespuestaReactivo {
    nOpcion?: number;
    cObservacion?: string;
}

// Mapea las respuestas ya guardadas para el token (RespuestasGuardadas, último
// recordset de CONFIG.SP_OBTEN_EVALUACION) al shape del estado `respuestas`
// (key `${nSeccion}-${nReactivo}`), para que al abrir la evaluación cada opción
// guardada aparezca seleccionada en su RadioGroup y la observación prellene su
// textarea. nValor solo se acepta si corresponde a una opción activa del
// reactivo: un valor huérfano no debe contar como respondido (inflaría "Total
// evaluado" y saciaría obligatorios sin radio visible). Filas sin opción ni
// observación se omiten; keys duplicados: gana la última (last-write-wins).
export const mapearRespuestasGuardadas = (
    evaluacion: EvaluacionDetalle,
): Record<string, RespuestaReactivo> => {
    const opcionesActivas = new Set(
        (evaluacion.ReactivosOpciones ?? [])
            .filter((opcion) => opcion.bActivo)
            .map((opcion) => `${opcion.nSeccion}-${opcion.nReactivo}-${opcion.nOpcion}`),
    );

    const respuestas: Record<string, RespuestaReactivo> = {};

    (evaluacion.RespuestasGuardadas ?? []).forEach((guardada) => {
        const key = `${guardada.nSeccion}-${guardada.nReactivo}`;
        const nOpcion = guardada.nValor != null
            && opcionesActivas.has(`${guardada.nSeccion}-${guardada.nReactivo}-${guardada.nValor}`)
            ? guardada.nValor
            : undefined;
        const cObservacionLimpia = guardada.cValor?.trim();
        // Trim: observación vacía o de puro whitespace se trata como ausente.
        const cObservacion = cObservacionLimpia ? cObservacionLimpia : undefined;

        if (nOpcion == null && cObservacion == null) return;

        const respuesta: RespuestaReactivo = {};
        if (nOpcion != null) respuesta.nOpcion = nOpcion;
        if (cObservacion != null) respuesta.cObservacion = cObservacion;
        respuestas[key] = respuesta;
    });

    return respuestas;
};

// Calcula la ponderación de una sección a partir de sus reactivos: el total es
// la suma del máximo nValorPonderacion de las opciones de cada reactivo
// puntuable (nTipo 1 con opciones activas), y el obtenido suma la ponderación
// de la opción seleccionada en respuestas (key `${nSeccion}-${nReactivo}`).
// Ambos se redondean a 4 decimales para evitar drift de flotantes
// (ej. 0.05 * 4 = 0.20000000000000004). Usa el Map precalculado de
// opcionesByOpcion para que el lookup de la opción seleccionada sea O(1) en
// lugar de O(O) — antes era el hotspot de re-render con muchos reactivos.
export const calcularPonderacionSeccion = (
    reactivos: ReactivoConOpciones[],
    respuestas: Record<string, RespuestaReactivo>,
): PonderacionSeccion => {
    let total = 0;
    let obtenido = 0;

    for (const { reactivo, opciones, opcionesByOpcion } of reactivos) {
        if (reactivo.nTipo !== 1 || opciones.length === 0) continue;

        // Total: máximo nValorPonderacion de las opciones del reactivo. Se
        // itera una vez con un for clásico (más rápido que Math.max sobre un
        // array) porque las opciones suelen ser ≤ 5.
        let max = opciones[0].nValorPonderacion;
        for (let j = 1; j < opciones.length; j += 1) {
            const v = opciones[j].nValorPonderacion;
            if (v > max) max = v;
        }
        total += max;

        // Obtenido: opción seleccionada por el usuario, lookup O(1) con el Map.
        const nOpcion = respuestas[`${reactivo.nSeccion}-${reactivo.nReactivo}`]?.nOpcion;
        if (nOpcion != null) {
            const seleccionada = opcionesByOpcion.get(nOpcion);
            if (seleccionada) {
                obtenido += seleccionada.nValorPonderacion;
            }
        }
    }

    return {
        total: +(total.toFixed(4)),
        obtenido: +(obtenido.toFixed(4)),
    };
};

// Indica si existe al menos una contestación (algún reactivo con nOpcion
// seleccionada). Habilita el botón Guardar. O(N) sobre los reactivos para no
// confundir con respuestas de texto (nTipo 2) que no deben habilitar Guardar
// si la evaluación es solo de reactivos abiertos.
export const hayRespuestas = (respuestas: Record<string, RespuestaReactivo>) => {
    for (const key in respuestas) {
        if (respuestas[key]?.nOpcion != null) return true;
    }
    return false;
};

// Detecta si una opción representa un "NO" (respuesta negativa que obliga a
// dejar observación). Match exacto de cOpcion normalizado: trim + uppercase
// contra el token 'NO'. Evita falsos positivos como "North" o "Note".
// No requiere cambios al backend: usa el shape actual de ReactivosOpciones.
export const esOpcionNegativa = (opcion: ReactivoOpcion | undefined): boolean =>
    opcion?.cOpcion?.trim().toUpperCase() === 'NO';

// Indica si la observación es obligatoria para una opción seleccionada.
// Combina dos señales: el flag BD `bRequiereObservacion` (CONFIG.EvaluacionesReactivosOpciones)
// y la heurística legacy `esOpcionNegativa` (opción cuyo texto es "NO"). Se
// combinan en vez de reemplazarse para no romper evaluaciones existentes
// cuyas opciones "NO" aún tienen `bRequiereObservacion=0` (default de la
// columna) — el comportamiento legacy se conserva hasta migrar la BD.
export const opcionRequiereObservacion = (opcion: ReactivoOpcion | undefined): boolean =>
    opcion?.bRequiereObservacion === true || esOpcionNegativa(opcion);

// True si la observación está vacía o ausente. Alineado con el trim que aplica
// mapearRespuestasGuardadas: una observación de puro whitespace se trata como
// no escrita.
export const observacionVacia = (cObservacion?: string): boolean =>
    !cObservacion || cObservacion.trim() === '';

// Cuenta los reactivos obligatorios pendientes de responder. Es obligatorio el
// reactivo respondible (nTipo 1 con opciones activas) cuyo bOpcional es falsy
// (semántica heredada del flujo viejo: SurveyWizard/helpers.ts handleDisable).
// Los no respondibles (otros nTipo / sin opciones) no cuentan: no pueden
// contestarse, así que no deben bloquear el envío.
// Además, suma 1 por cada reactivo con bMostrarObservaciones=true que tenga
// seleccionada una opción negativa ("NO") y observación vacía: en ese caso
// el textarea se vuelve obligatorio y debe bloquear el envío hasta escribirse.
// Para nTipo 2 (reactivo abierto), si bOpcional=0 y la observación está
// vacía también cuenta como pendiente: el textarea ES la única respuesta.
// Consume un array plano de reactivos (reactivosFlat) para evitar el
// flatMap que se ejecutaba en cada llamada.
export const contarObligatoriosPendientes = (
    reactivos: ReactivoConOpciones[],
    respuestas: Record<string, RespuestaReactivo>,
) => {
    let pendientes = 0;

    for (const { reactivo, opciones, opcionesByOpcion } of reactivos) {
        const key = `${reactivo.nSeccion}-${reactivo.nReactivo}`;
        const respuesta = respuestas[key];

        // nTipo 1: selección de opción (ponderable).
        if (reactivo.nTipo === 1 && opciones.length > 0) {
            const nOpcion = respuesta?.nOpcion;

            // Reactivo obligatorio sin opción seleccionada: pendiente.
            if (!Number(reactivo.bOpcional) && nOpcion == null) {
                pendientes += 1;
                continue;
            }

            // Reactivo con observación obligatoria (opción con
            // bRequiereObservacion=true o heurística legacy "NO" +
            // bMostrarObservaciones + observación vacía): pendiente.
            if (nOpcion != null && reactivo.bMostrarObservaciones) {
                const opcionSeleccionada = opcionesByOpcion.get(nOpcion);
                if (opcionRequiereObservacion(opcionSeleccionada) && observacionVacia(respuesta?.cObservacion)) {
                    pendientes += 1;
                }
            }
            continue;
        }

        // nTipo 2: reactivo abierto. Sin opciones; la única "contestación" es
        // la observación. Si bOpcional=0 y la observación está vacía, queda
        // pendiente. Si bOpcional=1 (o truthy) nunca bloquea.
        if (reactivo.nTipo === 2) {
            if (!Number(reactivo.bOpcional) && observacionVacia(respuesta?.cObservacion)) {
                pendientes += 1;
            }
            continue;
        }

        // Otros nTipo no son respondibles: ni bloquean ni cuentan.
    }

    return pendientes;
};

// Cuenta los reactivos respondibles (nTipo 1 con opciones activas) y cuántos ya
// tienen una opción seleccionada. Se usa en el footer para mostrar el porcentaje
// "total evaluado" (respondidos / total). No distingue obligatorios vs
// opcionales: el denominador refleja todas las preguntas que el usuario puede
// contestar, igual que el conteo que usa calcularPonderacionSeccion. Una sola
// pasada en lugar de filter+filter para reducir alocaciones.
export interface RespondiblesConteo {
    total: number;
    respondidos: number;
}

export const contarRespondiblesRespondidos = (
    reactivos: ReactivoConOpciones[],
    respuestas: Record<string, RespuestaReactivo>,
): RespondiblesConteo => {
    let total = 0;
    let respondidos = 0;

    for (const { reactivo, opciones } of reactivos) {
        if (reactivo.nTipo !== 1) continue;
        if (opciones.length === 0) continue;
        const nOpcion = respuestas[`${reactivo.nSeccion}-${reactivo.nReactivo}`]?.nOpcion;
        if (nOpcion == null) continue;
        total += 1;
        respondidos += 1;
    }

    return { total, respondidos };
};

// Agrega la ponderación de todas las secciones en un solo par (total, obtenido),
// redondeando a 4 decimales igual que calcularPonderacionSeccion para evitar
// drift de flotantes cuando se suman varias secciones.
export const calcularPonderacionGlobal = (
    secciones: SeccionConReactivos[],
    respuestas: Record<string, RespuestaReactivo>,
): PonderacionSeccion => {
    let total = 0;
    let obtenido = 0;
    secciones.forEach(({ reactivos }) => {
        const seccion = calcularPonderacionSeccion(reactivos, respuestas);
        total += seccion.total;
        obtenido += seccion.obtenido;
    });
    return {
        total: +(total.toFixed(4)),
        obtenido: +(obtenido.toFixed(4)),
    };
};

// --- Payload de /Guarda/Evaluacion -------------------------------------------
// Espejo del payload de encuestas (IDEncuesta + MetaData + Respuestas con el
// objeto completo de la pregunta): cada Respuesta lleva los datos del reactivo
// y su contestación. nValor = nOpcion elegida, cOpcion = texto de la opción,
// cValor = observación (equivalente a nRespuesta/cRespuesta/cValor del flujo
// viejo de encuestas).

export interface RespuestaEvaluacionPayload {
    nSeccion: number;
    nReactivo: number;
    nValor: number | null;
    cValor: string | null;
}

export interface GuardaEvaluacionPayload {
    IDEvaluacion: number;
    // Guardar → false (borrador), Enviar → true (finalizado). El backend aún
    // no implementa la ruta; por ahora solo se formatea la petición.
    // bFinalizado: boolean;
    // El view todavía no captura MetaInfo; la propiedad queda lista para
    // cuando exista el formulario (misma convención que el flujo de encuestas).
    // MetaData: unknown[];
    Respuestas: RespuestaEvaluacionPayload[];
}

// Arma el body del POST a /Guarda/Evaluacion: recorre las secciones activas en
// orden y solo incluye reactivos contestados (opción elegida u observación con
// contenido), igual que el flujo viejo no manda preguntas vacías. Usa el Map
// precalculado de opcionesByOpcion para que el lookup de la opción sea O(1).
export const construirPayloadGuardado = (
    evaluacion: EvaluacionDetalle,
    respuestas: Record<string, RespuestaReactivo>,
): GuardaEvaluacionPayload & { respuestasString: string } => {
    const Respuestas: RespuestaEvaluacionPayload[] = [];
    let respuestasString = '';

    for (const { reactivos } of agruparSecciones(evaluacion)) {
        for (const { reactivo, opcionesByOpcion } of reactivos) {
            const respuesta = respuestas[`${reactivo.nSeccion}-${reactivo.nReactivo}`];
            const opcion = respuesta?.nOpcion != null
                ? opcionesByOpcion.get(respuesta.nOpcion)
                : undefined;
            const cObservacion = respuesta?.cObservacion?.trim() ?? null;

            // Sin contestación no se envía la fila ni se concatena al string.
            if (opcion == null && cObservacion == null) continue;

            const payload: RespuestaEvaluacionPayload = {
                nSeccion: reactivo.nSeccion,
                nReactivo: reactivo.nReactivo,
                nValor: opcion?.nOpcion ?? null,
                cValor: cObservacion,
            };
            Respuestas.push(payload);

            if (respuestasString.length > 0) respuestasString += ';';
            respuestasString += `${payload.nSeccion},${payload.nReactivo},${payload.nValor ?? ''},${payload.cValor ?? ''}`;
        }
    }

    console.log('Respuestas a enviar: ', respuestasString);

    return {
        IDEvaluacion: evaluacion.nEvaluacion,
        Respuestas,
        respuestasString,
    };
};
